<script setup>
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const arrow = '→';
const arrowDefault = '→ default';

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...(flowDynamicVariables?.value ?? [])];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const cases = computed(() => props.modelValue?.cases ?? []);

function generateHandle(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || `case_${Date.now()}`;
}

function addCase() {
  update({ cases: [...cases.value, { value: '', handle: `case_${Date.now()}` }] });
}

function removeCase(i) {
  update({ cases: cases.value.filter((_, idx) => idx !== i) });
}

function updateCaseValue(i, value) {
  update({ cases: cases.value.map((c, idx) => idx === i ? { ...c, value, handle: generateHandle(value) } : c) });
}

// Variable picker – field
const showVarPicker = ref(false);
const varSearch = ref('');
const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q) || v.key.toLowerCase().includes(q));
});

function selectVar(key) {
  update({ field: `{{${key}}}` });
  showVarPicker.value = false;
  varSearch.value = '';
}
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Campo a avaliar -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SWITCH.EVALUATE_FIELD') }}</span>
      <div v-on-click-outside="() => { showVarPicker = false; varSearch = ''; }" class="relative">
        <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
          <input
            type="text"
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
            :value="modelValue?.field ?? ''"
            :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SWITCH.FIELD_PLACEHOLDER')"
            @input="update({ field: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="showVarPicker = !showVarPicker"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="showVarPicker"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                v-model="varSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                autofocus
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="selectVar(v.key)"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Casos -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SWITCH.CASES') }}</span>

      <div v-for="(c, i) in cases" :key="i" class="flex items-center gap-1.5 p-2 rounded-lg bg-n-alpha-1 border border-n-weak">
        <input
          class="flex-1 reset-base px-2 py-1.5 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-xs text-n-slate-12 placeholder:text-n-slate-9"
          :value="c.value"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SWITCH.CASE_VALUE_PLACEHOLDER')"
          @input="updateCaseValue(i, $event.target.value)"
        />
        <span class="text-[10px] text-n-slate-9 font-mono truncate max-w-[70px]">{{ arrow }} {{ c.handle }}</span>
        <button
          type="button"
          class="i-lucide-trash-2 size-4 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors"
          @click="removeCase(i)"
        />
      </div>

      <!-- Default (não removível) -->
      <div class="flex items-center gap-1.5 p-2 rounded-lg bg-n-alpha-1 border border-n-weak opacity-60">
        <span class="flex-1 text-xs text-n-slate-11 italic">{{ t('FLOW_BUILDER.ACTION_FORMS.SWITCH.DEFAULT') }}</span>
        <span class="text-[10px] text-n-slate-9 font-mono">{{ arrowDefault }}</span>
      </div>

      <Button
        ghost
        slate
        sm
        icon="i-lucide-plus"
        :label="t('FLOW_BUILDER.ACTION_FORMS.SWITCH.ADD_CASE')"
        class="self-start"
        @click="addCase"
      />
    </div>

  </div>
</template>
